import { store } from '@/app/store';
import type {
  PrintJobAccepted,
  PrintJobStatus,
  SessionImagesResponse,
} from '@/shared/types/api';
import {
  api,
  instrument,
  requireSessionId,
  trackApiError,
  unwrap,
} from '../client';

// The payment that paid for one copy and what the copy cost. When the copy
// fails, the backend refunds that amount only. A copy without a price has
// amount 0 and no payment intent.
export interface CopyPayment {
  paymentIntentId: string | null;
  amountCents: number;
}

export function submitPrintJob(
  portraitUuid: string,
  printerId: string,
  idempotencyKey: string,
  copyPayment: CopyPayment | null = null
): Promise<PrintJobAccepted> {
  return instrument<PrintJobAccepted>(
    {
      span: 'api.submit_print_job',
      errorTag: 'print_jobs_submit_print_job',
      tags: { 'api.image_uuid': portraitUuid, 'api.printer_id': printerId },
      before: {
        message: 'Submitting print job',
        // Full image UUID is intentional Sentry metadata for print-job joins.
        data: {
          image_uuid: portraitUuid,
          printer_id: printerId,
          idempotency_key: idempotencyKey,
        },
      },
      after: (result) => ({
        message: 'Print job accepted',
        data: { job_id: result?.job_id, printer_id: printerId },
      }),
      errorExtra: {
        image_uuid: portraitUuid,
        printer_id: printerId,
        idempotency_key: idempotencyKey,
      },
      trackErrorCode: 'api_error_printing',
    },
    () => {
      const sessionId = requireSessionId();
      return unwrap(
        api.POST('/v1/print-jobs', {
          body: {
            session_id: sessionId,
            image_id: portraitUuid,
            printer_name: printerId,
            idempotency_key: idempotencyKey,
            bucket_name: store.kiosk?.portrait_s3_bucket_name,
            ...(copyPayment && {
              amount_cents: copyPayment.amountCents,
              ...(copyPayment.paymentIntentId && {
                payment_intent_id: copyPayment.paymentIntentId,
              }),
            }),
          },
        })
      );
    }
  );
}

// Deliberately un-instrumented and silent: this fires every 1-3s while a job
// prints; the poll loop in Printing.js owns breadcrumbs, metrics, and error
// interpretation (404 = never accepted, anything else = transient).
export function getPrintJobStatus(jobKey: string): Promise<PrintJobStatus> {
  return unwrap(
    api.GET('/v1/print-jobs/{job_key}', {
      params: { path: { job_key: jobKey } },
      silent: true,
    })
  );
}

export function getPrintedPortraitsInSession(
  sessionId: string
): Promise<SessionImagesResponse> {
  return unwrap(
    api.GET('/v1/sessions/{session_id}/images', {
      params: { path: { session_id: sessionId } },
    })
  ).catch((error: Error) =>
    trackApiError(error, 'api_error_portraits_in_session')
  );
}
